import "./SignIn.css";
import { ReactComponent as FrameBack } from "../../images/frame_back.svg";
import { ReactComponent as MainLogo } from "./../../images/main-logo.svg";
import { ReactComponent as LockReset } from "./../../images/lock_reset.svg";
import { ReactComponent as CheckCircle } from "./../../images/check-circle-green.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChangeEvent, FocusEvent, useEffect, useState } from "react";
import { getErrorComponents } from "../../services/util";
import useSignInForm from "./utils";
import TalaeraSpinner from "../../components/TalaeraSpinner/TalaeraSpinner";
import TalaeraRoutes from "../../components/routes/TalaeraRoutes";
import PasswordRequirement from "components/PasswordRequirement";
import { populateUserSession, signIn } from "../../services/authUtil";
import queryString from "querystring";
import { isSetup } from "../../services/students";
import { PasswordInput } from "../../components/PasswordInput/PasswordInput";
import { useTranslation } from "react-i18next";

type Props = {
  verifyService: (
    args: any,
    onSuccess: (res: any) => void,
    onError: (errors: any) => void
  ) => void;
};

const PasswordReset = ({ verifyService }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    account,
    setAccount,
    errors: formErrors,
    touched,
    passwordStatus,
    setTouched,
    onValidate,
    onTouched,
  } = useSignInForm({ password: "", confirmPassword: "" });
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const parsed = queryString.parse(location.search.substring(1));
  const isNewUser = !!parsed.newuser;

  const handleChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const onError = (respBody: any) => {
    setErrors(respBody);
    setLoading(false);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const isValidated = onValidate();
    setTouched({
      email: true,
      password: true,
    });

    if (isValidated) {
      setErrors("");
      setLoading(true);
      verifyService(
        {
          token: parsed.token,
          passwd: account.password,
        },
        (resp: any) => {
          if (isNewUser) {
            signIn(
              {
                email: resp.email,
                password: account.password,
              },
              () => {
                populateUserSession((user: any) => {
                  if (
                    user &&
                    user.student &&
                    isSetup(user.student.status) &&
                    !user.roles.includes("team-manager")
                  ) {
                    navigate(TalaeraRoutes.ROOT.path);
                  }
                  setAccount({
                    password: "",
                    confirmPassword: "",
                  });
                  setLoading(false);
                }, onError);
              },
              onError
            );
          } else {
            setErrors("");
            setLoading(false);
            setResetDone(true);
          }
        },
        onError
      );
    }
  };

  useEffect(() => {
    if (location?.state?.error) {
      setErrors(location?.state?.error);
      navigate(location.pathname, { state: null, replace: true });
    }
    // eslint-disable-next-line
  }, [location]);

  return (
    <div className="sign-in-page reset-password">
      <div className="sign-in-content">
        <MainLogo />

        {errors && <div className="sign-in-error">{getErrorComponents(errors)}</div>}

        {!resetDone && (
          <>
            {isNewUser && (
              <div className="sign-in-header">
                <LockReset />
                <h1>{t("ForgotPassword.Set password")}</h1>
              </div>
            )}

            {!isNewUser && (
              <div className="sign-in-header">
                <h1>{t("ForgotPassword.Password Reset")}</h1>
              </div>
            )}

            <p className={"sign-in-description"}>
              Enter a new password below to change your password.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="form-wrapper">
                <PasswordInput
                  name="password"
                  label={t("ForgotPassword.Password")}
                  type="password"
                  variant="outlined"
                  placeholder={t("ForgotPassword.Enter your password")}
                  value={account.password}
                  error={!!(formErrors.password && touched.password)}
                  helperText={touched.password && formErrors.password}
                  onChange={handleChangeField}
                  onBlur={handleBlurField}
                />
                <PasswordInput
                  name="confirmPassword"
                  label={t("ForgotPassword.Confirm Password")}
                  type="password"
                  variant="outlined"
                  placeholder={t("ForgotPassword.Enter your password")}
                  value={account.confirmPassword}
                  error={!!(formErrors.confirmPassword && touched.confirmPassword)}
                  helperText={touched.confirmPassword && formErrors.confirmPassword}
                  onChange={handleChangeField}
                  onBlur={handleBlurField}
                />

                {account.password && (
                  <PasswordRequirement passwordStatus={passwordStatus} />
                )}

                {loading ? (
                  <div className="sign-in-spinner">
                    <TalaeraSpinner />
                  </div>
                ) : (
                  <button className="btn" type="submit">
                    {isNewUser ? "Set Password" : "Reset Password"}
                  </button>
                )}
              </div>
            </form>
          </>
        )}

        {resetDone && (
          <div className={"reset-pw-done-container"}>
            <CheckCircle />

            <h1>Password Changed!</h1>

            <p>Your password has been successfully changed!</p>

            <button
              className={"login-btn"}
              onClick={() =>
                navigate(TalaeraRoutes.SIGN_IN.path, { state: { resetDone: true } })
              }
            >
              Log back in
            </button>
          </div>
        )}
      </div>

      {!resetDone && (
        <div>
          {/* eslint-disable-next-line */}
          <Link to={TalaeraRoutes.SIGN_IN.path}>
            ← {t("ForgotPassword.Back to Login")}
          </Link>
        </div>
      )}

      <FrameBack className="sign-in-frame-back" />
    </div>
  );
};

export default PasswordReset;
